import React, {useState} from "react";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    SelectChangeEvent,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import CAlphaColorPicker from "crqlar-react-common/dist/components/CAlphaColorPicker";
import CColorPicker from "crqlar-react-common/dist/components/CColorPicker";
import CMemoryColorPicker from "crqlar-react-common/dist/components/CMemoryColorPicker";
import CountTextField from "crqlar-react-common/dist/components/CountTextField";
import CopyAdornment from "crqlar-react-common/dist/components/CopyAdornment";
import ModuleDatePicker from "crqlar-react-common/dist/components/ModuleDatePicker";
import NewColorPicker from "crqlar-react-common/dist/components/NewColorPicker";
import NewStatusPicker from "crqlar-react-common/dist/components/NewStatusPicker";
import Property from "crqlar-react-common/dist/components/Property";
import {ServiceStatus} from "crqlar-react-common/dist/models/service/serviceStatus.model";
import {getCountryISO2Cdes, getCountryNameForISO2} from "crqlar-react-common/dist/utils/country.utils";
import {convertLocalDateToDate, endOfDay, endOfWeek, formatDateLocalISO, startOfWeek} from "crqlar-react-common/dist/utils/date.util";
import {useTranslation} from "react-i18next";

import RadioCardPicker from "../../common/pickers/RadioCardPicker";

const STATUS_OPTIONS = ["Draft", "Active", "Archived"];

export default function MuiComponentsTestPage() {
    const {t} = useTranslation();

    const [name, setName] = useState("Anna Berger");
    const [status, setStatus] = useState("Active");
    const [category, setCategory] = useState("Restaurant");
    const [notificationsEnabled, setNotificationsEnabled] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [simpleColor, setSimpleColor] = useState("#1f7aec");
    const [alphaColor, setAlphaColor] = useState("#1f7aec99");
    const [memoryColor, setMemoryColor] = useState("#2ec4b6ff");
    const [colorPickerValue, setColorPickerValue] = useState<string | undefined>("#2EC4B6FF");
    const [filledColorPickerValue, setFilledColorPickerValue] = useState<string | undefined>("#3B82F6CC");
    const [standardColorPickerValue, setStandardColorPickerValue] = useState<string | undefined>("#8B5CF6FF");
    const [emptyColorPickerValue, setEmptyColorPickerValue] = useState<string | undefined>();
    const [transparentColorPickerValue, setTransparentColorPickerValue] = useState<string | undefined>("#00000000");
    const [autocompleteValue, setAutocompleteValue] = useState<string | undefined>("AT");
    const [autocompleteChipValues, setAutocompleteChipValues] = useState<string[]>(["AT", "IT"]);
    const [requiredColorPickerValue, setRequiredColorPickerValue] = useState<string | undefined>("#F59E0BFF");
    const [fieldValue, setFieldValue] = useState("Example value");
    const [datePickerValue, setDatePickerValue] = useState<string | undefined>("1990-06-15");
    const [dateRangeFrom, setDateRangeFrom] = useState<Date>(() => startOfWeek(new Date()));
    const [dateRangeTo, setDateRangeTo] = useState<Date>(() => endOfWeek(new Date()));
    const [dateRangeWithTodayFrom, setDateRangeWithTodayFrom] = useState<Date>(() => startOfWeek(new Date()));
    const [dateRangeWithTodayTo, setDateRangeWithTodayTo] = useState<Date>(() => endOfWeek(new Date()));
    const [childCount, setChildCount] = useState<number | undefined>(2);
    const [adultCount, setAdultCount] = useState<number | undefined>(1);
    const [statusPickerValue, setStatusPickerValue] = useState<ServiceStatus>(ServiceStatus.DRAFT);
    const [statusPickerValues, setStatusPickerValues] = useState<ServiceStatus[]>([
        ServiceStatus.ACTIVE,
        ServiceStatus.ACTIVE_NOT_BOOKABLE
    ]);

    const outlinedIconButtonSx = {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        width: 36,
        height: 36
    };

    const testAccordionSx = {
        borderRadius: 1,
        overflow: 'hidden',
        '&:before': {
            display: 'none'
        },
        '&.MuiAccordion-root': {
            borderRadius: 1,
            '&.Mui-expanded': {
                margin: 0
            }
        }
    };

    const moduleDatePickerSampleSx = {
        height: 'auto',
        justifyContent: 'flex-start',
        pl: 0
    };

    return (
        <Box
            sx={{
                width: "100%",
                // PageShell bounds the tab at 100dvh and clips it, so this page has to own its own scroller
                minHeight: 0,
                overflowY: "auto",
                bgcolor: "background.default",
                px: {xs: 2, md: 6},
                py: {xs: 3, md: 5},
            }}
        >
            <Stack gap={3} sx={{width: "100%", maxWidth: 960}}>
                <Box>
                    <Typography variant="h4">Frequently used MUI components</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                        Common controls and states used throughout the dashboard.
                    </Typography>
                </Box>

                <Card variant="outlined" sx={{p: {xs: 2, md: 3}}}>
                    <Stack gap={2.5}>
                        <Box>
                            <Typography variant="h5">Fields</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Text fields, autocomplete, and select controls.
                            </Typography>
                        </Box>

                        <Stack direction={{xs: "column", md: "row"}} gap={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Name"
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                            />
                            <Autocomplete
                                fullWidth
                                size="small"
                                options={STATUS_OPTIONS}
                                value={status}
                                onChange={(_, value) => setStatus(value ?? "")}
                                renderInput={(params) => <TextField {...params} label="Status"/>}
                            />
                            <FormControl fullWidth size="small">
                                <InputLabel id="mui-components-category-label">Category</InputLabel>
                                <Select
                                    labelId="mui-components-category-label"
                                    label="Category"
                                    value={category}
                                    onChange={(event: SelectChangeEvent) => setCategory(event.target.value)}
                                >
                                    <MenuItem value="Restaurant">Restaurant</MenuItem>
                                    <MenuItem value="Spa">Spa</MenuItem>
                                    <MenuItem value="Hotel">Hotel</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>

                        <Divider/>

                        <Box>
                            <Typography variant="subtitle1">Disabled notes</Typography>
                            <Typography variant="body2" color="text.secondary">
                                The same disabled value with a filled field label and a regular external label.
                            </Typography>
                        </Box>

                        <Stack direction={{xs: "column", md: "row"}} gap={2} alignItems="flex-start">
                            <TextField
                                fullWidth
                                disabled
                                multiline
                                minRows={3}
                                variant="filled"
                                label="Notes"
                                value="Guest prefers a quiet table near the window."
                            />

                            <FormControl fullWidth disabled>
                                <FormLabel htmlFor="mui-components-disabled-notes" sx={{mb: 0.75}}>
                                    Notes
                                </FormLabel>
                                <TextField
                                    id="mui-components-disabled-notes"
                                    disabled
                                    multiline
                                    minRows={3}
                                    value="Guest prefers a quiet table near the window."
                                />
                            </FormControl>
                        </Stack>
                    </Stack>
                </Card>

                <Card variant="outlined" sx={{p: {xs: 2, md: 3}}}>
                    <Stack gap={2.5}>
                        <Box>
                            <Typography variant="h5">Actions</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Button variants, icon actions, and disabled states.
                            </Typography>
                        </Box>

                        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                            <Button variant="contained" startIcon={<AddIcon/>}>Add</Button>
                            <Button variant="outlined" startIcon={<EditOutlinedIcon/>}>Edit</Button>
                            <Button variant="text">Cancel</Button>
                            <Button variant="contained" color="error" startIcon={<DeleteOutlineIcon/>}>
                                Delete
                            </Button>
                            <Button variant="contained" disabled>Disabled</Button>
                            <Tooltip title="More actions">
                                <IconButton aria-label="More actions">
                                    <MoreVertIcon/>
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                </Card>

                <Card variant="outlined" sx={{p: {xs: 2, md: 3}}}>
                    <Stack gap={2.5}>
                        <Box>
                            <Typography variant="h5">Selection controls</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Checkbox, switch, and radio controls with their labels.
                            </Typography>
                        </Box>

                        <Stack direction={{xs: "column", sm: "row"}} gap={{xs: 1, sm: 4}}>
                            <FormControlLabel control={<Checkbox defaultChecked/>} label="Send confirmation"/>
                            <FormControlLabel
                                control={(
                                    <Switch
                                        checked={notificationsEnabled}
                                        onChange={(event) => setNotificationsEnabled(event.target.checked)}
                                    />
                                )}
                                label="Notifications"
                            />
                            <FormControlLabel control={<Checkbox/>} label="Disabled" disabled/>
                        </Stack>

                        <FormControl>
                            <FormLabel id="mui-components-visibility-label">Visibility</FormLabel>
                            <RadioGroup row aria-labelledby="mui-components-visibility-label" defaultValue="public">
                                <FormControlLabel value="public" control={<Radio/>} label="Public"/>
                                <FormControlLabel value="private" control={<Radio/>} label="Private"/>
                                <FormControlLabel value="hidden" control={<Radio/>} label="Hidden" disabled/>
                            </RadioGroup>
                        </FormControl>
                    </Stack>
                </Card>

                <Card variant="outlined" sx={{p: {xs: 2, md: 3}}}>
                    <Stack gap={2.5}>
                        <Box>
                            <Typography variant="h5">Feedback and overlays</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Alerts, chips, loading feedback, and a basic dialog.
                            </Typography>
                        </Box>

                        <Stack gap={1}>
                            <Alert severity="success">Changes saved successfully.</Alert>
                            <Alert severity="warning" variant="outlined">This item has unsaved changes.</Alert>
                            <Alert severity="error" variant="filled">The request could not be completed.</Alert>
                        </Stack>

                        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                            <Chip label="Active" color="success" size="small"/>
                            <Chip label="Draft" variant="outlined" size="small"/>
                            <Chip label="Removable" onDelete={() => undefined} size="small"/>
                            <CircularProgress size={24}/>
                        </Stack>

                        <Button variant="outlined" sx={{alignSelf: "flex-start"}} onClick={() => setDialogOpen(true)}>
                            Open dialog
                        </Button>
                    </Stack>
                </Card>

                <Divider/>

                <Box>
                    <Typography variant="h4">Settings components</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                        Shared controls, states, and layout patterns used throughout settings forms.
                    </Typography>
                </Box>

                <Stack
            gap={3}
            divider={<Divider />}
            sx={{
              width: '100%',
              maxWidth: 960
            }}>

                    <Stack gap={2}>
                        <Typography variant="h5">{t('Colors')}</Typography>

                        <Stack gap={2}>
                            <Stack direction="row" alignItems="center" gap={2}>
                                <Typography sx={{ width: 180 }}>{t('Basic picker')}</Typography>
                                <CColorPicker value={simpleColor} onChange={setSimpleColor} size="small" />
                            </Stack>

                            <Stack direction="row" alignItems="center" gap={2}>
                                <Typography sx={{ width: 180 }}>{t('Alpha picker')}</Typography>
                                <CAlphaColorPicker color={alphaColor} onChange={setAlphaColor} />
                            </Stack>

                            <Stack direction="row" alignItems="center" gap={2}>
                                <Typography sx={{ width: 180 }}>{t('Memory picker')}</Typography>
                                <CMemoryColorPicker color={memoryColor} onChange={setMemoryColor} />
                            </Stack>
                        </Stack>
                    </Stack>

                    <Stack gap={2}>
                        <Typography variant="h5">{t('Alerts')}</Typography>

                        <Alert severity="warning" variant="outlined">
                            {t('This entity is managed by an external system and thus cannot be edited here.')}
                        </Alert>

                        <Alert severity="warning" variant="outlined">
                            {t('Leave this field empty to keep the current API key. Any value you enter here will override the current API key.')}
                        </Alert>

                        <Alert severity="info" variant="outlined">
                            {t('Resource assignment note')}
                        </Alert>

                        <Alert severity="info" variant="outlined">
                            {t('Please note down the secret key. You will not be able to see it again.')}
                        </Alert>

                        <Alert severity="success" variant="outlined">
                            {t('This cluster is safe to merge automatically.')}
                        </Alert>

                        <Alert severity="error" variant="outlined">
                            {t('Do not change this value if you are not sure what you are doing! It may cause data inconsistency. If you want to change it, please first delete all data from this connector')}
                        </Alert>

                        <Alert severity="info" variant="outlined">
                            <Stack gap={2}>
                                <Typography>
                                    {t('Select which option is used for each use case')}
                                </Typography>
                                <Button variant="outlined" size="small" sx={{ alignSelf: 'flex-start' }}>
                                    {t('Add item')}
                                </Button>
                            </Stack>
                        </Alert>

                        <Stack direction="row" gap={1} flexWrap="wrap">
                            <Alert severity="info" variant="filled" sx={{ flex: '1 1 160px' }}>
                                {t('Info')}
                            </Alert>
                            <Alert severity="success" variant="filled" sx={{ flex: '1 1 160px' }}>
                                {t('Success')}
                            </Alert>
                            <Alert severity="warning" variant="filled" sx={{ flex: '1 1 160px' }}>
                                {t('Warning')}
                            </Alert>
                            <Alert severity="error" variant="filled" sx={{ flex: '1 1 160px' }}>
                                {t('Error')}
                            </Alert>
                        </Stack>
                    </Stack>

                    <Stack gap={2}>
                        <Typography variant="h5">{t('Fields')}</Typography>

                        <Stack gap={1}>
                            <Property title={t('Text field')}>
                                <TextField
                    fullWidth
                    size="small"
                    value={fieldValue}
                    onChange={(event) => setFieldValue(event.target.value)} />

                            </Property>

                            <Property title={t('Text field error')}>
                                <TextField
                    fullWidth
                    size="small"
                    error
                    helperText={t('This field needs a valid value.')}
                    value={fieldValue}
                    onChange={(event) => setFieldValue(event.target.value)} />

                            </Property>

                            <Property title={t('Disabled text field')}>
                                <TextField
                    fullWidth
                    size="small"
                    disabled
                    value={fieldValue} />

                            </Property>

                            <Property title={t('Disabled text field with copy action')}>
                                <TextField
                    fullWidth
                    size="small"
                    disabled
                    value={fieldValue}
                    InputProps={{
                      endAdornment: <CopyAdornment value={fieldValue} />
                    }} />

                            </Property>

                            <Property title={t('Autocomplete')}>
                                <Autocomplete
                    id="test-autocomplete"
                    options={getCountryISO2Cdes()}
                    getOptionLabel={(option: any) => option ? getCountryNameForISO2(option) || option : ''}
                    value={autocompleteValue ?? null}
                    fullWidth
                    onChange={(_, value) => setAutocompleteValue(value ?? undefined)}
                    renderInput={(params) =>
                    <TextField
                      {...params}
                      fullWidth
                      size="small" />

                    } />

                            </Property>

                            <Property title={t('Autocomplete with chips')}>
                                <Autocomplete
                    multiple
                    id="test-autocomplete-with-chips"
                    options={getCountryISO2Cdes()}
                    getOptionLabel={(option: any) => option ? getCountryNameForISO2(option) || option : ''}
                    value={autocompleteChipValues}
                    fullWidth
                    onChange={(_, value) => setAutocompleteChipValues(value)}
                    renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });

                      return (
                        <Chip
                          key={key}
                          label={getCountryNameForISO2(option) || option}
                          size="small"
                          {...tagProps} />);


                    })
                    }
                    renderInput={(params) =>
                    <TextField
                      {...params}
                      fullWidth
                      size="small" />

                    } />

                            </Property>

                            <Property title={t('Disabled autocomplete')}>
                                <Autocomplete
                    id="test-autocomplete-disabled"
                    options={getCountryISO2Cdes()}
                    getOptionLabel={(option: any) => option ? getCountryNameForISO2(option) || option : ''}
                    value={autocompleteValue ?? null}
                    fullWidth
                    disabled
                    renderInput={(params) =>
                    <TextField
                      {...params}
                      fullWidth
                      size="small" />

                    } />

                            </Property>

                            <Property title={t('Date picker')}>
                                <ModuleDatePicker
                    value={convertLocalDateToDate(datePickerValue) ?? new Date()}
                    onChange={(date: Date) => setDatePickerValue(formatDateLocalISO(date))}
                    showStepButtons={false}
                    width={180}
                    sx={moduleDatePickerSampleSx} />

                            </Property>

                            <Property title={t('Disabled date picker')}>
                                <ModuleDatePicker
                    value={convertLocalDateToDate(datePickerValue) ?? new Date()}
                    onChange={(date: Date) => setDatePickerValue(formatDateLocalISO(date))}
                    disabled
                    showStepButtons={false}
                    width={180}
                    sx={moduleDatePickerSampleSx} />

                            </Property>

                            <Property title={t('Outlined color picker')}>
                                <NewColorPicker value={colorPickerValue} onChange={setColorPickerValue} isRequired={false} />
                            </Property>

                            <Property title={t('Filled color picker')}>
                                <NewColorPicker
                    value={filledColorPickerValue}
                    onChange={setFilledColorPickerValue}
                    variant="filled"
                    size="medium" />

                            </Property>

                            <Property title={t('Standard color picker')}>
                                <NewColorPicker
                    value={standardColorPickerValue}
                    onChange={setStandardColorPickerValue}
                    variant="standard" />

                            </Property>

                            <Property title={t('Empty color picker')}>
                                <NewColorPicker
                    value={emptyColorPickerValue}
                    onChange={setEmptyColorPickerValue} />

                            </Property>

                            <Property title={t('Transparent color picker')}>
                                <NewColorPicker
                    value={transparentColorPickerValue}
                    onChange={setTransparentColorPickerValue} />

                            </Property>

                            <Property title={t('Required color picker')}>
                                <NewColorPicker value={requiredColorPickerValue} onChange={setRequiredColorPickerValue} isRequired />
                            </Property>

                            <Property title={t('Disabled color picker')}>
                                <NewColorPicker value={colorPickerValue} onChange={setColorPickerValue} disabled />
                            </Property>
                        </Stack>
                    </Stack>

                    <Stack gap={2}>
                        <Typography variant="h5">{t('Date pickers')}</Typography>

                        <Stack gap={1}>
                            <Property title={t('Text field')}>
                                <TextField
                    fullWidth
                    size="small"
                    label={t('Select date')}
                    value={formatDateLocalISO(dateRangeFrom)}
                    InputProps={{
                      readOnly: true
                    }} />

                            </Property>

                            <Property title={t('Text field with action')}>
                                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                    gap={1}
                    sx={{ width: '100%' }}>

                                    <TextField
                      fullWidth
                      size="small"
                      label={t('Select date')}
                      value={formatDateLocalISO(dateRangeWithTodayFrom)}
                      InputProps={{
                        readOnly: true
                      }} />


                                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        const today = new Date();
                        setDateRangeWithTodayFrom(startOfWeek(today));
                        setDateRangeWithTodayTo(endOfWeek(today));
                      }}
                      sx={{
                        height: { xs: 36, sm: 40 },
                        px: 2
                      }}>

                                        {t('Today')}
                                    </Button>
                                </Stack>
                            </Property>

                            <Property title={t('Range picker')}>
                                <Stack gap={1} sx={{ width: '100%' }}>
                                    <ModuleDatePicker
                      variant="range"
                      value={{
                        start: dateRangeFrom,
                        end: dateRangeTo
                      }}
                      onChange={(range: {start: Date;end: Date;}) => {
                        setDateRangeFrom(range.start);
                        setDateRangeTo(endOfDay(range.end));
                      }}
                      width={320}
                      sx={moduleDatePickerSampleSx} />


                                    <Typography variant="body2" color="text.secondary">
                                        {t('Selected range')}: {formatDateLocalISO(dateRangeFrom)} - {formatDateLocalISO(dateRangeTo)}
                                    </Typography>
                                </Stack>
                            </Property>

                            <Property title={t('Range picker with action')}>
                                <Stack gap={1} sx={{ width: '100%' }}>
                                    <ModuleDatePicker
                      variant="range"
                      value={{
                        start: dateRangeWithTodayFrom,
                        end: dateRangeWithTodayTo
                      }}
                      onChange={(range: {start: Date;end: Date;}) => {
                        setDateRangeWithTodayFrom(range.start);
                        setDateRangeWithTodayTo(endOfDay(range.end));
                      }}
                      width={320}
                      sx={moduleDatePickerSampleSx} />


                                    <Typography variant="body2" color="text.secondary">
                                        {t('Selected range')}: {formatDateLocalISO(dateRangeWithTodayFrom)} - {formatDateLocalISO(dateRangeWithTodayTo)}
                                    </Typography>
                                </Stack>
                            </Property>

                            <Property title={t('Compact range picker')}>
                                <ModuleDatePicker
                    variant="range"
                    value={{
                      start: dateRangeFrom,
                      end: dateRangeTo
                    }}
                    onChange={(range: {start: Date;end: Date;}) => {
                      setDateRangeFrom(range.start);
                      setDateRangeTo(endOfDay(range.end));
                    }}
                    showWeekday={false}
                    showTodayButton={false}
                    width={208}
                    sx={moduleDatePickerSampleSx} />

                            </Property>

                            <Property title={t('Disabled range picker')}>
                                <ModuleDatePicker
                    variant="range"
                    value={{
                      start: dateRangeFrom,
                      end: dateRangeTo
                    }}
                    onChange={(range: {start: Date;end: Date;}) => {
                      setDateRangeFrom(range.start);
                      setDateRangeTo(endOfDay(range.end));
                    }}
                    disabled
                    width={320}
                    sx={moduleDatePickerSampleSx} />

                            </Property>
                        </Stack>
                    </Stack>

                    <Stack gap={2}>
                        <Typography variant="h5">NewStatusPicker</Typography>

                        <Stack gap={1}>
                            <Property title={t('Toggle buttons')}>
                                <ToggleButtonGroup
                    value={statusPickerValue}
                    exclusive
                    onChange={(_, newStatus) => {
                      if (newStatus !== null) {
                        setStatusPickerValue(newStatus);
                      }
                    }}
                    size="small">

                                    <ToggleButton value={ServiceStatus.DRAFT}>
                                        {t('Draft')}
                                    </ToggleButton>
                                    <ToggleButton value={ServiceStatus.ACTIVE}>
                                        {t('Active')}
                                    </ToggleButton>
                                    <ToggleButton value={ServiceStatus.ACTIVE_NOT_BOOKABLE}>
                                        {t('Active Not Bookable')}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Property>

                            <Property title={t('Status picker')}>
                                <NewStatusPicker<ServiceStatus>
                    value={statusPickerValue}
                    onChange={setStatusPickerValue}
                    ariaLabel={t('Status')}
                    options={[
                    { value: ServiceStatus.DRAFT, label: t('Draft') },
                    { value: ServiceStatus.ACTIVE, label: t('Active') },
                    { value: ServiceStatus.ACTIVE_NOT_BOOKABLE, label: t('Active Not Bookable') }]
                    } />

                            </Property>

                            <Property title={t('Status picker disabled')}>
                                <NewStatusPicker<ServiceStatus>
                    value={statusPickerValue}
                    onChange={setStatusPickerValue}
                    disabled
                    ariaLabel={t('Status')}
                    options={[
                    { value: ServiceStatus.DRAFT, label: t('Draft') },
                    { value: ServiceStatus.ACTIVE, label: t('Active') },
                    { value: ServiceStatus.ACTIVE_NOT_BOOKABLE, label: t('Active Not Bookable') }]
                    } />

                            </Property>

                            <Property title={t('Status picker with disabled option')}>
                                <NewStatusPicker<ServiceStatus>
                    value={statusPickerValue}
                    onChange={setStatusPickerValue}
                    ariaLabel={t('Status')}
                    options={[
                    { value: ServiceStatus.DRAFT, label: t('Draft') },
                    { value: ServiceStatus.ACTIVE, label: t('Active') },
                    { value: ServiceStatus.ACTIVE_NOT_BOOKABLE, label: t('Active Not Bookable'), disabled: true }]
                    } />

                            </Property>

                            <Property title={t('Status picker multi select')}>
                                <Stack gap={0.5}>
                                    <NewStatusPicker<ServiceStatus>
                    multiple
                    value={statusPickerValues}
                    onChange={setStatusPickerValues}
                    ariaLabel={t('Status')}
                    options={[
                    { value: ServiceStatus.DRAFT, label: t('Draft') },
                    { value: ServiceStatus.ACTIVE, label: t('Active') },
                    { value: ServiceStatus.ACTIVE_NOT_BOOKABLE, label: t('Active Not Bookable') }]
                    } />
                                    <Typography variant="caption" color="text.secondary">
                                        {t('Selected')}: {statusPickerValues.length ? statusPickerValues.join(', ') : t('none')}
                                    </Typography>
                                </Stack>
                            </Property>

                            <Property title={t('Status picker multi select disabled')}>
                                <NewStatusPicker<ServiceStatus>
                    multiple
                    value={statusPickerValues}
                    onChange={setStatusPickerValues}
                    disabled
                    ariaLabel={t('Status')}
                    options={[
                    { value: ServiceStatus.DRAFT, label: t('Draft') },
                    { value: ServiceStatus.ACTIVE, label: t('Active') },
                    { value: ServiceStatus.ACTIVE_NOT_BOOKABLE, label: t('Active Not Bookable') }]
                    } />
                            </Property>

                            <Property title={t('Radio cards')}>
                                <RadioCardPicker<ServiceStatus>
                    value={statusPickerValue}
                    onChange={setStatusPickerValue}
                    ariaLabel={t('Status')}
                    options={[
                    {
                      value: ServiceStatus.DRAFT,
                      label: t('Draft'),
                      description: t('Hidden from booking flows')
                    },
                    {
                      value: ServiceStatus.ACTIVE,
                      label: t('Active'),
                      description: t('Visible and bookable')
                    },
                    {
                      value: ServiceStatus.ACTIVE_NOT_BOOKABLE,
                      label: t('Active Not Bookable'),
                      description: t('Visible but not bookable')
                    }]
                    } />

                            </Property>
                        </Stack>
                    </Stack>

                    <Stack gap={2}>
                        <Typography variant="h5">{t('Buttons')}</Typography>

                        <Stack gap={1.5}>
                            <Typography variant="subtitle1" color="text.secondary">
                                {t('Button variants')}
                            </Typography>

                            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                <Button variant="contained" startIcon={<SaveIcon />}>
                                    {t('Save')}
                                </Button>

                                <Button variant="outlined" startIcon={<AddIcon />}>
                                    {t('Add')}
                                </Button>

                                <Button variant="text">
                                    {t('Cancel')}
                                </Button>

                                <Button variant="grayOutlined">
                                    {t('Outlined gray')}
                                </Button>

                                <Button variant="contained" color="error" startIcon={<DeleteOutlineIcon />}>
                                    {t('Delete')}
                                </Button>

                                <Button variant="contained" disabled startIcon={<SaveIcon />}>
                                    {t('Save')}
                                </Button>
                            </Stack>
                        </Stack>

                        <Stack gap={1.5}>
                            <Typography variant="subtitle1" color="text.secondary">
                                {t('Button sizes')}
                            </Typography>

                            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                <Button variant="contained" size="tiny">
                                    {t('Tiny')}
                                </Button>

                                <Button variant="contained" size="small">
                                    {t('Small')}
                                </Button>

                                <Button variant="contained">
                                    {t('Default')}
                                </Button>

                                <Button variant="contained" size="large">
                                    {t('Large')}
                                </Button>
                            </Stack>
                        </Stack>

                        <Stack gap={1.5}>
                            <Typography variant="subtitle1" color="text.secondary">
                                {t('Button colors')}
                            </Typography>

                            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                <Button variant="contained" color="primary">
                                    {t('Primary')}
                                </Button>

                                <Button variant="contained" color="secondary">
                                    {t('Secondary')}
                                </Button>

                                <Button variant="contained" color="success">
                                    {t('Success')}
                                </Button>

                                <Button variant="contained" color="warning">
                                    {t('Warning')}
                                </Button>

                                <Button variant="contained" color="info">
                                    {t('Info')}
                                </Button>
                            </Stack>
                        </Stack>

                        <Stack gap={1.5}>
                            <Typography variant="subtitle1" color="text.secondary">
                                {t('Button states')}
                            </Typography>

                            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                <Button variant="outlined" disabled>
                                    {t('Disabled')}
                                </Button>

                                <Button variant="text" disabled>
                                    {t('Disabled')}
                                </Button>

                                <Button variant="contained" color="error" disabled startIcon={<DeleteOutlineIcon />}>
                                    {t('Disabled')}
                                </Button>
                            </Stack>
                        </Stack>

                        <Stack gap={1.5}>
                            <Typography variant="subtitle1" color="text.secondary">
                                {t('Icon buttons')}
                            </Typography>

                            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                <Tooltip title={t('Back')}>
                                    <IconButton aria-label={t('Back')} sx={outlinedIconButtonSx}>
                                        <ArrowBackIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={t('Search')}>
                                    <IconButton aria-label={t('Search')} sx={outlinedIconButtonSx}>
                                        <SearchIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={t('More')}>
                                    <IconButton aria-label={t('More')}>
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={t('Delete')}>
                                    <IconButton aria-label={t('Delete')} color="error" sx={outlinedIconButtonSx}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                <IconButton aria-label={t('Disabled')} disabled sx={outlinedIconButtonSx}>
                                    <SaveIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        </Stack>

                        <Stack gap={1.5}>
                            <Typography variant="subtitle1" color="text.secondary">
                                {t('Icon button colors')}
                            </Typography>

                            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                <IconButton aria-label={t('Primary')} color="primary" sx={outlinedIconButtonSx}>
                                    <SaveIcon fontSize="small" />
                                </IconButton>

                                <IconButton aria-label={t('Info')} color="info" sx={outlinedIconButtonSx}>
                                    <SearchIcon fontSize="small" />
                                </IconButton>

                                <IconButton aria-label={t('Error')} color="error" sx={outlinedIconButtonSx}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Stack>

                    <Stack gap={2}>
                        <Typography variant="h5">{t('Counters')}</Typography>

                        <Stack gap={1}>
                            <Property title={t('Child')}>
                                <CountTextField
                    value={childCount}
                    onChangeValue={setChildCount}
                    sx={{ width: '50%' }}
                    size="small"
                    data-test-id="test-counter-child" />

                            </Property>

                            <Property title={t('Adult')}>
                                <CountTextField
                    value={adultCount}
                    onChangeValue={setAdultCount}
                    sx={{ width: '50%' }}
                    size="small"
                    data-test-id="test-counter-adult" />

                            </Property>

                            <Property title={t('Disabled')}>
                                <CountTextField
                    value={adultCount}
                    onChangeValue={setAdultCount}
                    disabled
                    sx={{ width: '50%' }}
                    size="small"
                    data-test-id="test-counter-disabled" />

                            </Property>
                        </Stack>
                    </Stack>

                    <Stack gap={2}>
                        <Typography variant="h5">{t('Accordions')}</Typography>

                        <Stack gap={1.5}>
                            <Accordion defaultExpanded variant="outlined" disableGutters sx={testAccordionSx}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Stack>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {t('Notification templates')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('Select which template is used for each event')}
                                        </Typography>
                                    </Stack>
                                </AccordionSummary>

                                <AccordionDetails>
                                    <Stack gap={1}>
                                        <Alert severity="info" variant="outlined">
                                            {t('No items configured yet.')}
                                        </Alert>

                                        <Stack direction="row" gap={1} flexWrap="wrap">
                                            <Chip label={t('Reservation Confirmed')} size="small" />
                                            <Chip label={t('Reservation Updated')} size="small" />
                                            <Chip label={t('Reservation Cancelled')} size="small" />
                                        </Stack>
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>

                            <Accordion variant="outlined" disableGutters sx={testAccordionSx}>
                                <AccordionSummary expandIcon={<ArrowDownwardIcon />}>
                                    {t('Custom styling options')}
                                </AccordionSummary>

                                <AccordionDetails>
                                    <Box
                      sx={{
                        width: '100%',
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        fontSize: '0.8rem'
                      }}>

                                        {`.crqlar-booking-widget-content {
      --crqlar-action-button-bg-color: #001b44;
    }`}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>

                            <AdditionalProperties title={t('Expandable fields')} defaultExpanded>
                                <Typography variant="body2" color="text.secondary">
                                    {t('Additional fields that only appear in specific workflows.')}
                                </Typography>
                                <Button variant="outlined" size="small" sx={{ alignSelf: 'flex-start' }}>
                                    {t('Add field')}
                                </Button>
                            </AdditionalProperties>

                            <AdditionalProperties title={t('Advanced settings')} variant="outlined">
                                <Typography variant="body2" color="text.secondary">
                                    {t('Configure optional behavior for advanced workflows.')}
                                </Typography>
                                <Alert severity="warning" variant="outlined">
                                    {t('Changing these settings may affect related data.')}
                                </Alert>
                            </AdditionalProperties>

                            <Accordion
                  defaultExpanded={false}
                  disableGutters
                  variant="outlined"
                  sx={testAccordionSx}>

                                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ backgroundColor: 'action.hover', px: 2, borderRadius: 'inherit' }}>

                                    <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" mr={1}>
                                        <Typography variant="subtitle1" fontWeight={500}>
                                            {t('Related items')} {t("(3)")}
                                        </Typography>
                                        <Button
                        variant="outlined"
                        size="small"
                        endIcon={<EditIcon />}
                        onClick={(event) => event.stopPropagation()}>

                                            {t('Edit')}
                                        </Button>
                                    </Stack>
                                </AccordionSummary>

                                <AccordionDetails sx={{ p: 2 }}>
                                    <Stack gap={1}>
                                        {['Item 1', 'Item 2', 'Item 3'].map((name) =>
                      <Stack key={name} direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography>{name}</Typography>
                                                <Chip label={t('Active')} size="small" />
                                            </Stack>
                      )}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Confirm changes</DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary">
                        This is the standard dialog structure used for focused actions.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => setDialogOpen(false)}>Confirm</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
